import { createSlice, PayloadAction } from '@reduxjs/toolkit';

type ClosedCaptionWithState = {
  name: string;
  identityId: string;
  caption: string;
  isFinal: boolean;
  showName?: boolean;
};

export type CaptionsState = {
  captions: ClosedCaptionWithState[];
  /**
   * Tracks whether captions are visible or hidden
   */
  isCaptionsOpen: boolean;
};

type UpdatePayload = {
  name: string;
  identityId: string;
  caption: string;
  isFinal: boolean;
  timestamp: number;
};

const initialState: CaptionsState = {
  captions: [],
  isCaptionsOpen: false,
};

export const captionsSlice = createSlice({
  name: 'captions',
  initialState,
  reducers: {
    updateCaptionText: (state, action: PayloadAction<UpdatePayload>) => {
      const { name, identityId, caption, isFinal } = action.payload;

      const captionIndex = state.captions.findLastIndex(
        (c) => c?.identityId === identityId,
      );

      const previousCaptionIndex = state.captions.length - 1;

      // Determine if this caption is from the same speaker as the previous one
      const isFromSameSpeakerAsPrevious =
        previousCaptionIndex >= 0 &&
        state.captions[previousCaptionIndex]?.identityId === identityId;

      if (captionIndex === -1) {
        // No existing caption for this user, create a new one
        state.captions.push({
          name,
          identityId,
          caption,
          isFinal,
          showName: true,
        });
      } else {
        const existingCaption = state.captions[captionIndex]!;

        if (isFinal) {
          // This indicates the end of this caption
          existingCaption.caption = caption;
          existingCaption.isFinal = true;
        } else if (existingCaption.isFinal) {
          // If the previous caption was final create a new caption (start of new caption)
          state.captions.push({
            name,
            identityId,
            caption,
            isFinal: false,
            showName: !isFromSameSpeakerAsPrevious,
          });
        } else {
          // Else, just update the latest non-final caption
          existingCaption.caption = caption;
        }
      }
    },

    setIsCaptionsOpen: (state, action: PayloadAction<{ open: boolean }>) => {
      state.isCaptionsOpen = action.payload.open;
    },

    clearCaptions: (state) => {
      state.captions = [];
    },
  },
});

export const { updateCaptionText, clearCaptions, setIsCaptionsOpen } =
  captionsSlice.actions;
